import type { CSSProperties, ReactNode } from 'react';
import { cn } from './cn';
import { Spinner } from './Button';

/**
 * Ant Design `Spin` replacement. Two modes:
 *  • standalone (`<Spin />`, `<Spin size="large" />`) — a centered brand spinner;
 *  • wrapping (`<Spin spinning={x}>children</Spin>`) — overlays a spinner over the
 *    (dimmed) children while `spinning`.
 */
export interface SpinProps {
  spinning?: boolean;
  size?: 'small' | 'default' | 'large';
  tip?: ReactNode;
  indicator?: ReactNode;
  className?: string;
  style?: CSSProperties;
  wrapperClassName?: string;
  fullscreen?: boolean;
  children?: ReactNode;
}

const SIZE: Record<string, string> = { small: 'size-4', default: 'size-6', large: 'size-8' };

export function Spin({
  spinning = true, size = 'default', tip, indicator, className, style, wrapperClassName, fullscreen, children,
}: SpinProps) {
  const spinner = indicator ?? <Spinner className={cn(SIZE[size] ?? SIZE.default, 'text-brand-600')} />;
  const body = (
    <div className="flex flex-col items-center gap-2">
      {spinner}
      {tip != null && <div className="text-sm text-muted">{tip}</div>}
    </div>
  );

  if (fullscreen) {
    return spinning ? (
      <div className="fixed inset-0 z-[var(--z-modal)] grid place-items-center bg-overlay backdrop-blur-[1px]">{body}</div>
    ) : null;
  }

  // Standalone — just the spinner (used for inline / centered loaders).
  if (children == null) {
    return (
      <span className={cn('inline-flex items-center justify-center gap-2', className)} style={style} role="status" aria-live="polite">
        {spinner}
        {tip != null && <span className="text-sm text-muted">{tip}</span>}
      </span>
    );
  }

  // Wrapping — overlay the spinner while spinning.
  return (
    <div className={cn('relative', wrapperClassName)}>
      {spinning && (
        <div className="absolute inset-0 z-10 grid place-items-center rounded-[inherit] bg-surface/60 backdrop-blur-[1px]">{body}</div>
      )}
      <div className={cn(spinning && 'pointer-events-none select-none opacity-60', className)} style={style}>{children}</div>
    </div>
  );
}
